import { useState, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import {
  Check,
  X,
  AlertCircle,
  Eye,
  ChevronDown,
  ChevronUp,
  MessageSquare,
  Flag,
  Pencil,
} from "lucide-react";

// ============================================================================
// Types
// ============================================================================

export type GradingStatus = "correct" | "incorrect" | "needs_review" | "skipped";

export interface QuestionAnswer {
  questionId: string;
  questionType: string;
  questionPrompt: string;
  studentAnswer: string | null;
  correctAnswer: string | null;
  autoGradable: boolean;
  autoGradingResult: GradingStatus;
  points: number;
  maxPoints: number;
  outcomeId?: string;
  outcomeName?: string;
  extractedData?: Record<string, unknown>;
}

export interface TeacherOverride {
  questionId: string;
  overrideStatus: GradingStatus | null;
  teacherNote: string;
  isFlagged: boolean;
}

export interface QuestionResultProps {
  answer: QuestionAnswer;
  override?: TeacherOverride;
  onOverrideChange?: (override: TeacherOverride) => void;
  isExpanded?: boolean;
  onExpandToggle?: () => void;
  className?: string;
}

// ============================================================================
// Helper Functions
// ============================================================================

function getStatusIcon(status: GradingStatus, size: "sm" | "lg" = "sm") {
  const iconSize = size === "lg" ? "h-6 w-6" : "h-4 w-4";

  switch (status) {
    case "correct":
      return <Check className={cn(iconSize, "text-green-600")} />;
    case "incorrect":
      return <X className={cn(iconSize, "text-red-600")} />;
    case "needs_review":
      return <Eye className={cn(iconSize, "text-amber-600")} />;
    case "skipped":
      return <AlertCircle className={cn(iconSize, "text-muted-foreground")} />;
  }
}

function getStatusBadge(status: GradingStatus, isOverride: boolean = false) {
  const variants: Record<GradingStatus, { label: string; className: string }> = {
    correct: {
      label: "Correct",
      className: "bg-green-100 text-green-800 border-green-200",
    },
    incorrect: {
      label: "Incorrect",
      className: "bg-red-100 text-red-800 border-red-200",
    },
    needs_review: {
      label: "Needs Review",
      className: "bg-amber-100 text-amber-800 border-amber-200",
    },
    skipped: {
      label: "Skipped",
      className: "bg-gray-100 text-gray-600 border-gray-200",
    },
  };

  const { label, className } = variants[status];

  return (
    <Badge variant="outline" className={cn(className, "font-medium")}>
      {isOverride && <Pencil className="h-3 w-3 mr-1" />}
      {label}
    </Badge>
  );
}

function formatAnswer(answer: string | null): string {
  if (answer === null || answer === undefined) {
    return "(no answer)";
  }
  if (typeof answer === "object") {
    return JSON.stringify(answer);
  }
  return String(answer);
}

// ============================================================================
// Sub-Components
// ============================================================================

interface StatusIndicatorProps {
  status: GradingStatus;
  isOverride: boolean;
}

function StatusIndicator({ status, isOverride }: StatusIndicatorProps) {
  const bgColors: Record<GradingStatus, string> = {
    correct: "bg-green-500",
    incorrect: "bg-red-500",
    needs_review: "bg-amber-500",
    skipped: "bg-gray-400",
  };

  return (
    <div
      className={cn(
        "flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-full flex-shrink-0",
        bgColors[status],
        isOverride && "ring-2 ring-offset-2 ring-primary"
      )}
      aria-label={`Status: ${status}${isOverride ? " (teacher override)" : ""}`}
    >
      {getStatusIcon(status, "lg")}
    </div>
  );
}

interface TeacherOverrideControlsProps {
  questionId: string;
  currentStatus: GradingStatus;
  override: TeacherOverride;
  onOverrideChange: (override: TeacherOverride) => void;
}

function TeacherOverrideControls({
  questionId,
  currentStatus,
  override,
  onOverrideChange,
}: TeacherOverrideControlsProps) {
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [noteText, setNoteText] = useState(override.teacherNote);

  const handleStatusOverride = useCallback(
    (newStatus: GradingStatus | null) => {
      onOverrideChange({
        ...override,
        questionId,
        overrideStatus: newStatus,
      });
    },
    [override, questionId, onOverrideChange]
  );

  const handleFlagToggle = useCallback(() => {
    onOverrideChange({
      ...override,
      questionId,
      isFlagged: !override.isFlagged,
    });
  }, [override, questionId, onOverrideChange]);

  const handleNoteSave = useCallback(() => {
    onOverrideChange({
      ...override,
      questionId,
      teacherNote: noteText,
    });
    setIsAddingNote(false);
  }, [override, questionId, noteText, onOverrideChange]);

  const effectiveStatus = override.overrideStatus ?? currentStatus;

  return (
    <div className="space-y-3 pt-3 border-t">
      <div className="text-sm font-medium text-muted-foreground">
        Teacher Actions
      </div>

      {/* Override buttons */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant={effectiveStatus === "correct" ? "default" : "outline"}
          size="sm"
          onClick={() =>
            handleStatusOverride(
              effectiveStatus === "correct" && override.overrideStatus
                ? null
                : "correct"
            )
          }
          className={cn(
            effectiveStatus === "correct" &&
              "bg-green-600 hover:bg-green-700 text-white"
          )}
          aria-label="Mark as correct"
        >
          <Check className="h-4 w-4 mr-1" />
          Correct
        </Button>

        <Button
          variant={effectiveStatus === "incorrect" ? "default" : "outline"}
          size="sm"
          onClick={() =>
            handleStatusOverride(
              effectiveStatus === "incorrect" && override.overrideStatus
                ? null
                : "incorrect"
            )
          }
          className={cn(
            effectiveStatus === "incorrect" &&
              "bg-red-600 hover:bg-red-700 text-white"
          )}
          aria-label="Mark as incorrect"
        >
          <X className="h-4 w-4 mr-1" />
          Incorrect
        </Button>

        <Button
          variant={override.isFlagged ? "default" : "outline"}
          size="sm"
          onClick={handleFlagToggle}
          className={cn(
            override.isFlagged && "bg-amber-600 hover:bg-amber-700 text-white"
          )}
          aria-label={override.isFlagged ? "Remove flag" : "Flag for review"}
        >
          <Flag className="h-4 w-4 mr-1" />
          {override.isFlagged ? "Flagged" : "Flag"}
        </Button>

        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsAddingNote(!isAddingNote)}
          aria-label={isAddingNote ? "Cancel note" : "Add note"}
        >
          <MessageSquare className="h-4 w-4 mr-1" />
          {override.teacherNote ? "Edit Note" : "Add Note"}
        </Button>
      </div>

      {/* Note input */}
      {isAddingNote && (
        <div className="space-y-2">
          <Textarea
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder="Add a note for this question..."
            className="min-h-[80px] text-sm"
            aria-label="Teacher note"
          />
          <div className="flex gap-2">
            <Button size="sm" onClick={handleNoteSave}>
              Save Note
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setNoteText(override.teacherNote);
                setIsAddingNote(false);
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Display existing note */}
      {override.teacherNote && !isAddingNote && (
        <div className="text-sm bg-muted/50 rounded-md p-3">
          <div className="flex items-center gap-1 text-muted-foreground mb-1">
            <MessageSquare className="h-3 w-3" />
            <span className="font-medium">Note:</span>
          </div>
          <p className="text-foreground">{override.teacherNote}</p>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function QuestionResult({
  answer,
  override,
  onOverrideChange,
  isExpanded = false,
  onExpandToggle,
  className,
}: QuestionResultProps) {
  // Determine effective status (override takes precedence)
  const effectiveStatus = override?.overrideStatus ?? answer.autoGradingResult;
  const hasOverride = override?.overrideStatus !== null && override?.overrideStatus !== undefined;

  // Calculate points based on effective status
  const earnedPoints =
    effectiveStatus === "correct"
      ? answer.maxPoints
      : effectiveStatus === "incorrect"
      ? 0
      : answer.points;

  // Default override if not provided
  const currentOverride: TeacherOverride = override ?? {
    questionId: answer.questionId,
    overrideStatus: null,
    teacherNote: "",
    isFlagged: false,
  };

  return (
    <Card
      className={cn(
        "transition-all",
        override?.isFlagged && "ring-2 ring-amber-400",
        className
      )}
    >
      <CardContent className="p-3 sm:p-4">
        {/* Main row - always visible */}
        <div className="flex items-start gap-3">
          {/* Status indicator */}
          <StatusIndicator status={effectiveStatus} isOverride={hasOverride} />

          {/* Question info */}
          <div className="flex-1 min-w-0 space-y-1">
            {/* Question prompt */}
            <div className="flex items-start justify-between gap-2">
              <p className="font-medium text-sm sm:text-base line-clamp-2">
                {answer.questionPrompt}
              </p>

              {/* Points badge - mobile-friendly */}
              <Badge
                variant="secondary"
                className="flex-shrink-0 text-xs sm:text-sm"
              >
                {earnedPoints}/{answer.maxPoints} pt
              </Badge>
            </div>

            {/* Status and type badges */}
            <div className="flex flex-wrap items-center gap-2">
              {getStatusBadge(effectiveStatus, hasOverride)}

              {!answer.autoGradable && (
                <Badge variant="outline" className="text-xs">
                  <Eye className="h-3 w-3 mr-1" />
                  Manual
                </Badge>
              )}

              {override?.isFlagged && (
                <Badge
                  variant="outline"
                  className="text-xs bg-amber-50 text-amber-700 border-amber-200"
                >
                  <Flag className="h-3 w-3 mr-1" />
                  Flagged
                </Badge>
              )}
            </div>

            {/* Compact answer display (when collapsed) */}
            {!isExpanded && (
              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 text-sm mt-2">
                <span className="text-muted-foreground">
                  <span className="font-medium">Answer:</span>{" "}
                  <span className="text-foreground">
                    {formatAnswer(answer.studentAnswer)}
                  </span>
                </span>
                {answer.correctAnswer && effectiveStatus === "incorrect" && (
                  <span className="text-green-700">
                    <span className="font-medium">Expected:</span>{" "}
                    {formatAnswer(answer.correctAnswer)}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Expand/collapse button */}
          {onExpandToggle && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onExpandToggle}
              className="flex-shrink-0"
              aria-label={isExpanded ? "Collapse details" : "Expand details"}
              aria-expanded={isExpanded}
            >
              {isExpanded ? (
                <ChevronUp className="h-5 w-5" />
              ) : (
                <ChevronDown className="h-5 w-5" />
              )}
            </Button>
          )}
        </div>

        {/* Expanded details */}
        {isExpanded && (
          <div className="mt-4 space-y-4 pl-0 sm:pl-[52px]">
            {/* Answer comparison */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* Student answer */}
              <div className="space-y-1">
                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Student Answer
                </div>
                <div
                  className={cn(
                    "p-3 rounded-md border text-sm",
                    effectiveStatus === "correct"
                      ? "bg-green-50 border-green-200"
                      : effectiveStatus === "incorrect"
                      ? "bg-red-50 border-red-200"
                      : "bg-muted/30 border-border"
                  )}
                >
                  {formatAnswer(answer.studentAnswer)}
                </div>
              </div>

              {/* Correct answer */}
              {answer.correctAnswer && (
                <div className="space-y-1">
                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Expected Answer
                  </div>
                  <div className="p-3 rounded-md border bg-muted/30 text-sm">
                    {formatAnswer(answer.correctAnswer)}
                  </div>
                </div>
              )}
            </div>

            {/* Curriculum outcome link */}
            {answer.outcomeId && answer.outcomeName && (
              <div className="text-sm">
                <span className="text-muted-foreground">Curriculum: </span>
                <span className="font-medium">{answer.outcomeId}</span>
                <span className="text-muted-foreground"> - {answer.outcomeName}</span>
              </div>
            )}

            {/* Teacher override controls */}
            {onOverrideChange && (
              <TeacherOverrideControls
                questionId={answer.questionId}
                currentStatus={answer.autoGradingResult}
                override={currentOverride}
                onOverrideChange={onOverrideChange}
              />
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
